/**
 * Shared Escape handling for DOM modal overlays.
 *
 * Tippy menus always win when visible (see tippy-utils). Open modals form a
 * LIFO stack so nested overlays close from the top without falling through to
 * the glyph canvas or other view-level Escape handlers.
 *
 * The stack and capture listener live on `window` so every webpack copy of
 * this module (static vs async chunks) shares one order.
 */
import { hasVisibleTippyMenus } from '../tippy-utils';
function getRuntime() {
    const holder = window;
    if (!holder.__modalEscapeRuntime) {
        holder.__modalEscapeRuntime = {
            stack: [],
            listenerInstalled: false
        };
    }
    return holder.__modalEscapeRuntime;
}
function installEscapeListener() {
    const runtime = getRuntime();
    if (runtime.listenerInstalled) {
        return;
    }
    document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') {
            return;
        }
        // Tippy menus consume Escape first.
        if (hasVisibleTippyMenus()) {
            return;
        }
        const { stack } = getRuntime();
        while (stack.length > 0) {
            const top = stack[stack.length - 1];
            if (!top) {
                break;
            }
            if (top.isOpen && !top.isOpen()) {
                stack.pop();
                continue;
            }
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            // Pop before close so a re-entrant bind from close/open is clean.
            stack.pop();
            top.close();
            scheduleFocusedViewRestore();
            return;
        }
    }, true);
    runtime.listenerInstalled = true;
}
/**
 * Register a modal so Escape closes it (LIFO).
 * Call `release()` when the modal closes by any means (button, backdrop, etc.).
 */
export function bindModalEscape(close, options) {
    installEscapeListener();
    const { stack } = getRuntime();
    const id = Symbol('modal-escape');
    const entry = {
        id,
        close,
        isOpen: options?.isOpen
    };
    stack.push(entry);
    let released = false;
    return {
        release: () => {
            if (released) {
                return;
            }
            released = true;
            const runtimeStack = getRuntime().stack;
            const index = runtimeStack.findIndex((candidate) => candidate.id === id);
            if (index >= 0) {
                runtimeStack.splice(index, 1);
            }
            scheduleFocusedViewRestore();
        }
    };
}
function scheduleFocusedViewRestore() {
    queueMicrotask(() => {
        if (hasBoundModalEscape()) {
            return;
        }
        window.restoreFocusedViewDomFocus?.();
    });
}
/** Whether any modal is currently bound for Escape dismissal. */
export function hasBoundModalEscape() {
    return getRuntime().stack.some((entry) => !entry.isOpen || entry.isOpen());
}
